import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import {
  sendBusinessPhoneVerificationCodeAPI,
  verifyBusinessPhoneCodeAPI,
} from "@/services/api/user/businessSignup";
import Modal from "@/components/Modal";
import { AUTH_CARD_CLASS } from "@/components/auth/authStyles";
import {
  buildBusinessSignupQuery,
  readBusinessSignupParams,
} from "./businessSignupState";

const PHONE_STATUS = {
  PHONE_EXISTED: -20012,
  INVALID_PHONE: -20013,
  SMS_NOT_CONFIGURED: -20014,
  CODE_EXPIRED: -20015,
  CODE_INVALID: -20016,
  TOO_MANY_ATTEMPTS: -20017,
  SMS_SEND_FAILED: -20018,
  CODE_ALREADY_USED: -20023,
};

const BusinessPhoneVerifyPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = readBusinessSignupParams(search);

  const missingHandoff =
    params.emailVerified !== "1" ||
    !params.username ||
    !params.companyEmail ||
    !params.phone ||
    !params.regionCode ||
    !params.password;

  const [phoneCode, setPhoneCode] = useState("");
  const [phoneSeconds, setPhoneSeconds] = useState(0);
  const [phoneVerified, setPhoneVerified] = useState(
    params.phoneVerified === "1",
  );
  const [phoneBusy, setPhoneBusy] = useState(false);
  const autoSentRef = useRef(false);

  const [modalAlertVisible, setModalAlertVisible] = useState(false);
  const [modalAlertText, setModalAlertText] = useState("");

  useEffect(() => {
    if (missingHandoff) {
      navigate(
        `/business/signup/verify${buildBusinessSignupQuery({ ...params })}`,
        { replace: true },
      );
    }
  }, [missingHandoff]);

  useEffect(() => {
    const myInterval = setInterval(() => {
      setPhoneSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(myInterval);
  }, []);

  const showAlert = (text: string) => {
    setModalAlertText(t(text));
    setModalAlertVisible(true);
  };

  const errorText = (resp: { result?: unknown } | undefined, fallback: string) =>
    typeof resp?.result === "string" && resp.result ? resp.result : fallback;

  const onSendPhoneCode = async () => {
    if (!params.phone) {
      return showAlert("Please fill in all the blank");
    }
    setPhoneBusy(true);
    const resp = await sendBusinessPhoneVerificationCodeAPI(
      params.phone,
      params.regionCode || "",
    );
    setPhoneBusy(false);
    if (resp?.status === 0) {
      setPhoneSeconds(60);
      setPhoneVerified(false);
      return;
    }
    if (resp?.status === PHONE_STATUS.PHONE_EXISTED) {
      return showAlert("Phone Existed");
    }
    if (resp?.status === PHONE_STATUS.INVALID_PHONE) {
      return showAlert("Invalid Phone Number");
    }
    if (resp?.status === PHONE_STATUS.SMS_NOT_CONFIGURED) {
      return showAlert("SMS service not configured");
    }
    showAlert(errorText(resp, "Network error, please try again"));
  };

  useEffect(() => {
    if (missingHandoff || phoneVerified || autoSentRef.current) {
      return;
    }
    autoSentRef.current = true;
    onSendPhoneCode();
  }, [missingHandoff, phoneVerified]);

  const verifyPhoneCode = async () => {
    if (!params.phone || !phoneCode.trim()) {
      showAlert("Please enter the SMS code");
      return false;
    }
    setPhoneBusy(true);
    const resp = await verifyBusinessPhoneCodeAPI(
      params.phone,
      params.regionCode || "",
      phoneCode.trim(),
    );
    setPhoneBusy(false);
    if (resp?.status === 0 && resp?.result?.verified) {
      setPhoneVerified(true);
      return true;
    }
    if (resp?.status === PHONE_STATUS.CODE_EXPIRED) {
      showAlert("Verification code expired, please resend");
      return false;
    }
    if (resp?.status === PHONE_STATUS.CODE_INVALID) {
      showAlert("Invalid verification code");
      return false;
    }
    if (resp?.status === PHONE_STATUS.TOO_MANY_ATTEMPTS) {
      showAlert("Too many attempts, please resend");
      return false;
    }
    if (resp?.status === PHONE_STATUS.CODE_ALREADY_USED) {
      showAlert("Verification code already used, please resend");
      return false;
    }
    if (resp?.status === PHONE_STATUS.INVALID_PHONE) {
      showAlert("Invalid Phone Number");
      return false;
    }
    showAlert(errorText(resp, "Network error, please try again"));
    return false;
  };

  const goNext = async () => {
    if (!phoneVerified) {
      const verified = await verifyPhoneCode();
      if (!verified) {
        return;
      }
    }
    navigate(
      `/business/signup/profile${buildBusinessSignupQuery({
        ...params,
        phoneVerified: "1",
      })}`,
    );
  };

  if (missingHandoff) {
    return null;
  }

  return (
    <div className={AUTH_CARD_CLASS}>
      <h1 className="text-2xl font-extrabold text-[#333]">
        {t("Phone Verification")}
      </h1>
      <p className="mt-1 text-sm text-gray-500">
        {phoneVerified
          ? t("Verified")
          : t("Enter the SMS code sent to your phone")}
        <br />
        <span className="mt-1 inline-flex items-center gap-2">
          <span className="font-semibold text-[#333]">{params.phone}</span>
          {phoneVerified && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
              <Check size={12} strokeWidth={3} />
              {t("Verified")}
            </span>
          )}
        </span>
      </p>

      <div className="mt-6 space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t("SMS code")}
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={phoneCode}
            disabled={phoneVerified}
            onChange={(e) => {
              setPhoneCode(e.target.value);
              if (phoneVerified) setPhoneVerified(false);
            }}
            onKeyDown={(e) => e.key === "Enter" && !phoneVerified && goNext()}
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-center text-lg tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-[#ffa217] disabled:bg-gray-50 disabled:text-gray-400"
            placeholder="000000"
          />
        </div>

        {phoneSeconds === 0 ? (
          <button
            type="button"
            onClick={onSendPhoneCode}
            disabled={phoneBusy}
            className="w-full rounded-lg border border-red-400 py-2.5 text-sm font-bold text-red-500 transition hover:bg-red-50 disabled:opacity-50"
          >
            {t("SEND CODE")}
          </button>
        ) : (
          <div className="py-2.5 text-center text-sm text-gray-500">
            {phoneSeconds} {t("s")}
          </div>
        )}
      </div>

      <div className="mt-8 space-y-4">
        <button
          type="button"
          onClick={goNext}
          disabled={phoneBusy}
          className="w-full rounded-lg bg-[#ffa217] py-3 font-bold text-white transition hover:bg-[#e08f0e] disabled:opacity-50"
        >
          {t("Continue")}
        </button>

        <button
          type="button"
          onClick={() =>
            navigate(
              `/business/signup/verify${buildBusinessSignupQuery({ ...params })}`,
            )
          }
          className="w-full rounded-lg bg-red-500 py-3 font-bold text-white transition hover:bg-red-600"
        >
          {t("BACK")}
        </button>
      </div>

      <Modal visible={modalAlertVisible} onClose={() => setModalAlertVisible(false)}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-[#333]">{modalAlertText}</div>
          <button
            type="button"
            onClick={() => setModalAlertVisible(false)}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default BusinessPhoneVerifyPage;
